import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SubscriptionItemsComponent } from '../../Partials/subscription-items/subscription-items.component';
import { SubscriptionConfirmDialogComponent } from '../../Dialogs/subscription-confirm-dialog/subscription-confirm-dialog.component';
import { TrialExtendDialogComponent } from '../../Dialogs/trial-extend-dialog/trial-extend-dialog.component';
import { AvEmptyStateComponent } from '../../../../Shared/Components/av-empty-state/av-empty-state.component';
import { AvStatusChipComponent } from '../../../../Shared/Components/av-status-chip/av-status-chip.component';
import { SubscriptionControlFacade } from '../../../../../Facades/Subscriptions/subscription-control.facade';
import { DATE_FORMAT, DATE_TIME_FORMAT, VALUE_UNRESOLVED } from '../../../../../Consts/backoffice.const';
import {
  CANCEL_CONFIRM_TITLE,
  CANCEL_MODE_CONSEQUENCE,
  CANCEL_MODE_LABEL,
  CANCEL_MODE_OPTIONS,
  CANCEL_NO_REFUND_NOTE,
  CHANGE_AMOUNT_DUE_LABEL,
  CHANGE_AMOUNT_SUMMARY_KEY,
  CHANGE_CHARGES_NOTHING,
  CHANGE_CHARGES_NOW,
  CHANGE_CONFIRM_TITLE,
  CHANGE_CONSEQUENCE_KEY,
  CHANGE_PREVIEW_REQUIRED,
  CONFIRM_BUTTON,
  CONFIRM_NO_AMOUNT,
  OFFER_TYPE_LABEL,
  OFFER_TYPE_OPTIONS,
  PAUSE_CONFIRM_TITLE,
  PAUSE_CONSEQUENCE,
  RECURRING_ITEM_KEY,
  RESUME_CONFIRM_TITLE,
  RESUME_CONSEQUENCE,
  TRIAL_MAX_DAYS,
} from '../../../../../Consts/subscription-control.const';
import {
  PRICE_COMPONENT_LABEL,
  PriceComponentType,
} from '../../../../../Enums/price-component-type.enum';
import {
  SubscriptionConfirmDialogResult,
  TrialExtendDialogResult,
} from '../../../Interfaces/subscription-dialog.interface';

@Component({
  selector: 'app-subscription-control',
  standalone: true,
  imports: [
    RouterLink,
    DatePipe,
    DecimalPipe,
    MatIconModule,
    MatTooltipModule,
    TranslateModule,
    SubscriptionItemsComponent,
    AvEmptyStateComponent,
    AvStatusChipComponent,
  ],
  templateUrl: './subscription-control.component.html',
  styleUrl: './subscription-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'd-block' },
})
export class SubscriptionControlComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _dialog = inject(MatDialog);
  private readonly _translate = inject(TranslateService);

  readonly facade = inject(SubscriptionControlFacade);

  readonly dateFormat = DATE_FORMAT;
  readonly dateTimeFormat = DATE_TIME_FORMAT;
  readonly unresolved = VALUE_UNRESOLVED;
  readonly offerTypeOptions = OFFER_TYPE_OPTIONS;
  readonly offerTypeLabels = OFFER_TYPE_LABEL;

  private readonly _params = toSignal(this._route.paramMap);

  readonly organizationLink = computed<string>(
    () => `/organizations/${this._params()?.get('id') ?? ''}`,
  );

  constructor() {
    this._registerOrganizationParam();
  }

  selectPlan(planId: string): void {
    this.facade.selectPlan(planId);
  }

  selectOfferType(offerType: string): void {
    this.facade.selectOfferType(offerType);
  }

  requestPreview(): void {
    this.facade.requestPreview();
  }

  reload(): void {
    this.facade.reload();
  }

  openApplyChange(): void {
    const reference = this._dialog.open(SubscriptionConfirmDialogComponent, {
      data: {
        title: CHANGE_CONFIRM_TITLE,
        consequence: this._changeConsequence(),
        organizationName: this.facade.organizationName(),
        amountLabel: this._previewAmountLabel(),
        confirmLabel: CONFIRM_BUTTON,
        isCommitmentLocked: this.facade.isCommitmentLocked(),
        canOverrideCommitment: this.facade.hasRole(),
        modes: [],
        modeLabels: {},
        modeConsequences: {},
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: SubscriptionConfirmDialogResult) => this._applyChange(result));
  }

  openPause(): void {
    const reference = this._dialog.open(SubscriptionConfirmDialogComponent, {
      data: {
        title: PAUSE_CONFIRM_TITLE,
        consequence: PAUSE_CONSEQUENCE,
        organizationName: this.facade.organizationName(),
        amountLabel: CONFIRM_NO_AMOUNT,
        confirmLabel: CONFIRM_BUTTON,
        isCommitmentLocked: false,
        canOverrideCommitment: false,
        modes: [],
        modeLabels: {},
        modeConsequences: {},
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: SubscriptionConfirmDialogResult) => this._pause(result));
  }

  openResume(): void {
    const reference = this._dialog.open(SubscriptionConfirmDialogComponent, {
      data: {
        title: RESUME_CONFIRM_TITLE,
        consequence: RESUME_CONSEQUENCE,
        organizationName: this.facade.organizationName(),
        amountLabel: CONFIRM_NO_AMOUNT,
        confirmLabel: CONFIRM_BUTTON,
        isCommitmentLocked: false,
        canOverrideCommitment: false,
        modes: [],
        modeLabels: {},
        modeConsequences: {},
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: SubscriptionConfirmDialogResult) => this._resume(result));
  }

  openCancel(): void {
    const reference = this._dialog.open(SubscriptionConfirmDialogComponent, {
      data: {
        title: CANCEL_CONFIRM_TITLE,
        consequence: CANCEL_NO_REFUND_NOTE,
        organizationName: this.facade.organizationName(),
        amountLabel: this._recurringAmountLabel(),
        confirmLabel: CONFIRM_BUTTON,
        isCommitmentLocked: this.facade.isCommitmentLocked(),
        canOverrideCommitment: this.facade.hasRole(),
        modes: CANCEL_MODE_OPTIONS,
        modeLabels: CANCEL_MODE_LABEL,
        modeConsequences: CANCEL_MODE_CONSEQUENCE,
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: SubscriptionConfirmDialogResult) => this._cancel(result));
  }

  openTrial(): void {
    const reference = this._dialog.open(TrialExtendDialogComponent, {
      data: {
        organizationName: this.facade.organizationName(),
        currentTrialEnd: this.facade.subscription()?.trial_ends_at ?? null,
        maxTrialEnd: this._maxTrialEnd(),
      },
    });

    reference
      .afterClosed()
      .subscribe((result?: TrialExtendDialogResult) => this._extendTrial(result));
  }

  private _registerOrganizationParam(): void {
    effect(() => {
      this.facade.load(this._params()?.get('id') ?? '');
    });
  }

  private _changeConsequence(): string {
    return this._translate.instant(CHANGE_CONSEQUENCE_KEY, {
      lead: this._translate.instant(
        this.facade.preview()?.chargesNow ? CHANGE_CHARGES_NOW : CHANGE_CHARGES_NOTHING,
      ),
      note: this._translate.instant(CHANGE_PREVIEW_REQUIRED),
    });
  }

  private _previewAmountLabel(): string {
    const preview = this.facade.preview();

    if (preview === null) {
      return CONFIRM_NO_AMOUNT;
    }

    return this._translate.instant(CHANGE_AMOUNT_SUMMARY_KEY, {
      amountDueLabel: this._translate.instant(CHANGE_AMOUNT_DUE_LABEL),
      amountDue: preview.amount_due_now ?? 0,
      nextTotal: preview.next_invoice_total ?? 0,
      currency: preview.currency,
    });
  }

  private _recurringAmountLabel(): string {
    const items = this.facade.items();

    if (items.length === 0) {
      return CONFIRM_NO_AMOUNT;
    }

    return items
      .map((item) =>
        this._translate.instant(RECURRING_ITEM_KEY, {
          component: this._translate.instant(
            PRICE_COMPONENT_LABEL[item.component_type as PriceComponentType] ??
              item.component_type,
          ),
          quantity: item.quantity,
          amount: item.unit_amount,
          currency: item.currency ?? '',
        }),
      )
      .join(', ');
  }

  private _maxTrialEnd(): string {
    const limit = new Date();

    limit.setDate(limit.getDate() + TRIAL_MAX_DAYS);

    return limit.toISOString().slice(0, 10);
  }

  private _applyChange(result?: SubscriptionConfirmDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.applyChange(result.overrideCommitmentLock, result.overrideReason);
  }

  private _pause(result?: SubscriptionConfirmDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.pause(result.reason);
  }

  private _resume(result?: SubscriptionConfirmDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.resume(result.reason);
  }

  private _cancel(result?: SubscriptionConfirmDialogResult): void {
    if (!result || result.mode === null) {
      return;
    }

    this.facade.cancel({
      mode: result.mode,
      reason: result.reason,
      override_commitment_lock: result.overrideCommitmentLock,
      override_reason: result.overrideReason ?? undefined,
    });
  }

  private _extendTrial(result?: TrialExtendDialogResult): void {
    if (!result) {
      return;
    }

    this.facade.extendTrial({ trial_end: result.trialEnd, reason: result.reason });
  }
}
